/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleType,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  dictionaryValueFormatter,
  EntityInputDialogComponent,
  EntityInputDialogConfig,
  rendererIconByName,
  ROW_BOLD_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  ActionFlow,
  createDialogRoute,
  DateTimeTypes,
  EnumDictionary,
  FormStateSnapshot,
  getRouteInitData,
  getRouteParam,
  getRouteStaticData,
  HelpID,
  setEntityField,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { ID, WithUniqueId } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';

import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get, lowerCase } from 'lodash';

import {
  SchedulingScheduleTemplateTaskBusinessService,
} from '../../resources/scheduling-schedule-template-task-business.service';
import {
  DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE,
  getScheduleTemplateTaskNoteId,
  getScheduleTemplateTaskTodoId,
  isExistsCcResourceGeneralSubtab,
  isExistsUserResourceGeneralSubtab,
  ScheduleTemplateTaskCcResourceListItem,
  ScheduleTemplateTaskDetails,
  ScheduleTemplateTaskDetailsGeneralSubtabName,
  ScheduleTemplateTaskDetailsTabName,
  ScheduleTemplateTaskInfo,
  ScheduleTemplateTaskNoteListItem,
  ScheduleTemplateTaskTodoDialogConfig,
  ScheduleTemplateTaskTodoListItem,
  ScheduleTemplateTaskUserResourceListItem,
} from '../../resources/scheduling-schedule-template-task.model';

import {
  SchedulingScheduleTemplateTaskDetailsFormState,
} from './scheduling-schedule-template-task-details-form.service';

import {
  scheduleTemplateTaskTodoDialog,
} from './scheduling-schedule-template-task-todo-dialog/scheduling-schedule-template-task-todo-dialog.route';

export const scheduleTemplateNoteListItemDialog = createDialogRoute({
  component: EntityInputDialogComponent,
  init: SchedulingScheduleTemplateTaskBusinessService,
});

@Component({
  standalone: false,
  selector: 'gh-scheduling-schedule-template-task-details',
  templateUrl: 'scheduling-schedule-template-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SchedulingScheduleTemplateTaskDetailsFormState,
  ],
})
export class SchedulingScheduleTemplateTaskDetailsComponent extends AbstractFormComponent<ScheduleTemplateTaskDetails>
  implements OnInit {

  scheduleTemplateTaskDetailsTabName = ScheduleTemplateTaskDetailsTabName;
  scheduleTemplateTaskDetailsGeneralSubtabName = ScheduleTemplateTaskDetailsGeneralSubtabName;

  scheduleTemplateType: ScheduleType;
  changeDateAfterCompleteTypes: EnumDictionary<ScheduleTaskChangeDateAfterCompleteType>;
  scheduleTaskTypes: EnumDictionary<ScheduleTaskType>;
  scheduleTemplateTaskTodoEventType: EnumDictionary<ScheduleTaskTodoEventType>;
  systemRoles: EnumDictionary<UserSystemRole>;
  taskDetails: ScheduleTemplateTaskDetails;
  ccResourceGridDefinition: GridDefinition;
  userResourceGridDefinition: GridDefinition;
  notesGridDefinition: LayoutBasedGridDefinition;
  todoGridDefinition: LayoutBasedGridDefinition;
  taskId: ID;
  templateId: ID;

  parentTitleLabels = {
    [ScheduleType.Construction]: {
      [FormMode.CreateMode]: 'lbl_create_construction_sch_template',
      [FormMode.EditMode]: 'lbl_edit_construction_sch_template',
      [FormMode.ViewMode]: 'lbl_construction_sch_template',
    },
    [ScheduleType.Sales]: {
      [FormMode.CreateMode]: 'lbl_create_sales_sch_template',
      [FormMode.EditMode]: 'lbl_edit_sales_sch_template',
      [FormMode.ViewMode]: 'lbl_sales_sch_template',
    },
    [ScheduleType.PreConstruction]: {
      [FormMode.CreateMode]: 'lbl_create_pre_construction_sch_template',
      [FormMode.EditMode]: 'lbl_edit_pre_construction_sch_template',
      [FormMode.ViewMode]: 'lbl_pre_construction_sch_template',
    },
    [ScheduleType.Maintenance]: {
      [FormMode.CreateMode]: 'lbl_create_maintenance_sch_template',
      [FormMode.EditMode]: 'lbl_edit_maintenance_sch_template',
      [FormMode.ViewMode]: 'lbl_maintenance_sch_template_task',
    },
  };

  pageTitleLabels = {
    [ScheduleType.Construction]: 'lbl_construction_sch_template_task',
    [ScheduleType.Sales]: 'lbl_sales_sch_template_task',
    [ScheduleType.PreConstruction]: 'lbl_pre_construction_sch_template_task',
    [ScheduleType.Maintenance]: 'lbl_maintenance_sch_template_task',
  };

  documentParentTitleId = {
    [ScheduleType.Construction]: 'lbl_construction_sch_template',
    [ScheduleType.Sales]: 'lbl_sales_sch_template',
    [ScheduleType.PreConstruction]: 'lbl_pre_construction_sch_template',
    [ScheduleType.Maintenance]: 'lbl_maintenance_sch_template',
  };

  helpIds = {
    [ScheduleType.Construction]: 'setup/schedules/constructions/tasks/details',
    [ScheduleType.Sales]: 'setup/schedules/sales/tasks/details',
    [ScheduleType.PreConstruction]: 'setup/schedules/pre-constructions/tasks/details',
    [ScheduleType.Maintenance]: 'setup/schedules/warranty/tasks/details',
  };

  constructor(public form: SchedulingScheduleTemplateTaskDetailsFormState,
              private businessService: SchedulingScheduleTemplateTaskBusinessService,
              private userContext: BuilderUserContext,
              private gridDefinitionFactory: GridDefinitionFactory,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private translationService: TranslationService,
              private dialogService: DialogService,
              private route: ActivatedRoute) {
    super();
  }

  get parentTitle(): string {
    return this.parentTitleLabels[this.scheduleTemplateType][this.form.mode];
  }

  get pageTitle(): string {
    return this.pageTitleLabels[this.scheduleTemplateType];
  }

  get helpId(): HelpID {
    return this.helpIds[this.scheduleTemplateType];
  }

  get documentParentTitle(): string {
    return this.documentParentTitleId[this.scheduleTemplateType];
  }

  get isExistsCcResourceGeneralSubtab(): boolean {
    return isExistsCcResourceGeneralSubtab(this.taskDetails.type, this.taskDetails.isIncludedTasksExist);
  }

  get isExistsUserResourceGeneralSubtab(): boolean {
    return isExistsUserResourceGeneralSubtab(this.taskDetails.type, this.taskDetails.isIncludedTasksExist);
  }
  get parentUrl(): string | undefined {
    const route: string = this.route.snapshot.queryParams?.parentRoute;
    if (!route) {
      return undefined;
    }
    if (route.includes('/new/gantt-chart') && this.templateId) {
      return route.replace('/new/gantt-chart', `/${this.templateId}/gantt-chart`);
    }
    return undefined;
  }
  get completeFlow(): ActionFlow<FormFlowState<ScheduleTemplateTaskDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.form.isTopFormInvalid,
        () => this.handleErrors()),
      execute: this.commonActions.execute((state: FormFlowState<ScheduleTemplateTaskDetails>) =>
        this.businessService.update(this.templateId, this.taskId, state.value, this.scheduleTemplateType)),
      finish: ({ value }: FormFlowState<ScheduleTemplateTaskDetails>) => {
        this.taskDetails = value;
        this.form.build(value, this.isFormEditable());
      },
    });
  }

  handleErrors(): void {
    this.form.highlightInfoSnapshot();
    this.form.selectTab(ScheduleTemplateTaskDetailsTabName.General);
  }

  hasPermission(permission: string): boolean {
    switch (this.scheduleTemplateType) {
      case ScheduleType.Construction:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.ConstructionTemplates[permission]);
      case ScheduleType.Sales:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.SalesTemplates[permission]);
      case ScheduleType.PreConstruction:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.PreConstructionTemplates[permission]);
      case ScheduleType.Maintenance:
        return this.userContext.hasPermission(BuilderPermission.Setup.Schedules.MaintenanceTemplates[permission]);
      default:
        return false;
    }
  }

  ngOnInit(): void {
    const {
      changeDateAfterCompleteTypes,
      taskDetails,
      scheduleTaskTypes,
      scheduleTemplateTaskTodoEventType,
      noteGridLayout,
      todoGridLayout,
      systemRoles,
    } = getRouteInitData(this.route);
    const { type: scheduleTemplateType } = getRouteStaticData(this.route);
    this.templateId = getRouteParam(this.route, 'templateId');
    this.taskId = getRouteParam(this.route, 'taskId');

    this.systemRoles = systemRoles;
    this.scheduleTemplateType = scheduleTemplateType;
    this.taskDetails = taskDetails;
    this.scheduleTaskTypes = scheduleTaskTypes;
    this.scheduleTemplateTaskTodoEventType = scheduleTemplateTaskTodoEventType;
    this.changeDateAfterCompleteTypes = changeDateAfterCompleteTypes;
    this.ccResourceGridDefinition = this.buildCcResourceGridDefinition();
    this.userResourceGridDefinition = this.buildUserResourceGridDefinition();
    this.notesGridDefinition = this.buildNotesGridDefinition();
    this.notesGridDefinition.bind(noteGridLayout);
    this.todoGridDefinition = this.buildTodoGridDefinition();
    this.todoGridDefinition.bind(todoGridLayout);

    this.form.build(taskDetails, this.isFormEditable());
  }

  onCostCenterResourceChange({ supplierName, supplierId, costCenterId }: ScheduleTemplateTaskCcResourceListItem): void {
    if (this.form.editMode) {
      this.form.updateTaskInfoSnapshot(setFormStateSnapshotValue(
        this.form.info,
        setEntityField(this.form.info.value, 'resource', supplierName),
      ));
      this.form.updateSupplierContactId(costCenterId, supplierId);
    }
  }

  onUserResourceChange({ name, userId }: ScheduleTemplateTaskUserResourceListItem): void {
    if (this.form.editMode) {
      this.form.updateTaskInfoSnapshot(setFormStateSnapshotValue(
        this.form.info,
        setEntityField(this.form.info.value, 'resource', name),
      ));
      this.form.updateUserId(userId);
    }
  }

  onSelectedTabChanged(tabName: ScheduleTemplateTaskDetailsTabName): void {
    this.form.selectTab(tabName);
  }

  onSelectedGeneralSubtabChanged(subtabName: ScheduleTemplateTaskDetailsGeneralSubtabName): void {
    this.form.selectGeneralSubtab(subtabName);
  }

  onTopFormDataChange(infoSnapshot: FormStateSnapshot<ScheduleTemplateTaskInfo>): void {
    this.form.updateTaskInfoSnapshot(infoSnapshot);
  }

  onDeleteNote(note: ScheduleTemplateTaskNoteListItem): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_sch_task_note_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeNote(note));
  }

  onEditNote(note: ScheduleTemplateTaskNoteListItem): void {
    this.dialogService.openWithInput(scheduleTemplateNoteListItemDialog,
                                     <EntityInputDialogConfig<ScheduleTemplateTaskNoteListItem>>{
        entity: note,
        titleId: this.form.viewMode ? 'lbl_note' : 'lbl_edit_note',
        placeholderId: 'lbl_note',
        field: 'memo',
        required: true,
        showTextArea: true,
        id: note.templateTaskNoteId,
        minHeight: 300,
        readOnly: this.form.viewMode,
      }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedNote: ScheduleTemplateTaskNoteListItem) => this.form.updateNote(note, updatedNote));
  }

  onAddNote(): void {
    this.dialogService.openWithInput(scheduleTemplateNoteListItemDialog,
                                     <EntityInputDialogConfig<ScheduleTemplateTaskNoteListItem>>{
        titleId: 'lbl_add_note',
        placeholderId: 'lbl_note',
        saveButtonLabelId: 'lbl_add',
        field: 'memo',
        required: true,
        showTextArea: true,
        minHeight: 300,
      }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((newNote: ScheduleTemplateTaskNoteListItem) => this.form.addNote(newNote));
  }

  onAddTodo(): void {
    this.dialogService.openWithInput(scheduleTemplateTaskTodoDialog, <ScheduleTemplateTaskTodoDialogConfig>{
      scheduleTemplateType: this.scheduleTemplateType,
      event: ScheduleTaskTodoEventType.BeforeStart,
      days: DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE,
      mode: FormMode.CreateMode,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((newTodo: ScheduleTemplateTaskTodoListItem) => this.form.addTodo(newTodo));
  }

  onEditTodo(todo: WithUniqueId<ScheduleTemplateTaskTodoListItem>): void {
    this.dialogService.openWithInput(scheduleTemplateTaskTodoDialog, <ScheduleTemplateTaskTodoDialogConfig>{
      scheduleTemplateType: this.scheduleTemplateType,
      templateTaskTodoId: todo.templateTaskTodoId,
      days: todo.days,
      event: todo.event,
      systemRole: todo.systemRole,
      memo: todo.memo,
      user: todo.user,
      mode: this.form.editable ? FormMode.EditMode : FormMode.ViewMode,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((editedTodo: WithUniqueId<ScheduleTemplateTaskTodoListItem>) =>
        this.form.updateTodo(todo, editedTodo));
  }

  onDeleteTodo(todo: WithUniqueId<ScheduleTemplateTaskTodoListItem>): void {
    this.dialogService.confirmation(yesCancelConfirmation('msg_sch_task_todo_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeTodo(todo));
  }

  onClearResource(): void {
    if (this.form.editMode) {
      this.form.updateTaskInfoSnapshot(setFormStateSnapshotValue(
        this.form.info,
        setEntityField(this.form.info.value, 'resource', ''),
      ));
      this.form.updateUserId('');
    }
  }

  private isFormEditable(): boolean {
    return this.hasPermission('EDIT');
  }

  private buildCcResourceGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'code',
      width: 150,
      headerName: this.translationService.get('lbl_number'),
    }, {
      field: 'name',
      width: 300,
      headerName: this.translationService.get('lbl_description'),
    }, {
      field: 'supplierName',
      width: 300,
      headerName: this.translationService.get('lbl_name'),
    }, {
      field: 'supplierPhone',
      width: 200,
      headerName: this.translationService.get('lbl_phone'),
    }, {
      field: 'supplierContact',
      width: 400,
      headerName: this.translationService.get('lbl_contact'),
    }, {
      field: 'isPreferred',
      width: 150,
      headerName: this.translationService.get('lbl_preferred'),
      cellRenderer: truthyCellRenderer(rendererIconByName('done')),
    }],                                           {
      rowClassRules: {
        [ROW_BOLD_CLASS]: ({ data }: any) => !!data.isPreferred || !!data.isCcSupplier,
      },
      getRowId: (params) =>
        String(`${params.data.supplierId}${params.data.costCenterId}`),
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
    });
  }

  private buildUserResourceGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'name',
      width: 300,
    }],                                           {
      getRowId: (params) => String(params.data.userId),
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
    });
  }

  private buildNotesGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased({
      addedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
    },                                                 {
      getRowId: (params) => String(getScheduleTemplateTaskNoteId(params.data)),
    });
  }

  private buildTodoGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased({
      systemRole: {
        valueFormatter: dictionaryValueFormatter(this.systemRoles),
      },
      addedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      user: {
        valueFormatter: this.todoGridUserValueFormatter.bind(this),
      },
      event: {
        valueGetter: this.todoGridEventValueGetter.bind(this),
      },
    },                                                 {
      getRowId: (params) => String(getScheduleTemplateTaskTodoId(params.data)),
    });
  }

  /**
   * The value formatter of Event column of Todos Grid
   * @param params ValueFormatterParams
   */
  private todoGridEventValueGetter(params: ValueGetterParams): string {
    const { days } = params.data;
    const dayLabel = this.translationService.get(days === 1 ? 'lbl_day' : 'lbl_days');
    return lowerCase(`${days} ${dayLabel} ${this.scheduleTemplateTaskTodoEventType.get(params.data.event)}`);
  }

  /**
   * The value formatter of User column of Todos Grid
   * @param params ValueFormatterParams
   */
  private todoGridUserValueFormatter(params: ValueFormatterParams): string {
    return get(params.data.user, 'userName', '');
  }
}
